import { DestroyRef, effect, inject, Injector, untracked } from '@angular/core'
import { FieldState, FieldTree } from '@angular/forms/signals'
import { Observable } from 'rxjs'

type Kuuntelija = (arvo: unknown, ilmoita: boolean) => void

const kuuntelijat = new WeakMap<FieldState<unknown>, Set<Kuuntelija>>()

/**
 * Lomakekentän arvon muutokset tilaamisen jälkeen, kuten reaktiivisen lomakkeen `valueChanges`: tilaushetken arvoa ei
 * lähetetä. `asetaArvo`lla asetettu arvo välittyy heti, myös silloin kun se ei muutu. Käyttäjän syöttämä arvo havaitaan
 * efektissä, joten se välittyy muutostunnistuksessa eikä vielä syötetapahtuman aikana. Virta päättyy, kun injektorin
 * omistaja (yleensä komponentti) tuhotaan, joten tilausta ei tarvitse lopettaa erikseen.
 */
export function arvonMuutokset<T>(kentta: FieldTree<T>, asetukset?: { injector?: Injector }): Observable<T> {
  const injector = asetukset?.injector ?? inject(Injector)
  return new Observable<T>(tilaaja => {
    const tila = kentta() as FieldState<unknown>
    let edellinen = untracked(() => tila.value())
    const kuuntelija: Kuuntelija = (arvo, ilmoita) => {
      edellinen = arvo
      if (ilmoita) {
        tilaaja.next(arvo as T)
      }
    }
    let kentanKuuntelijat = kuuntelijat.get(tila)
    if (!kentanKuuntelijat) {
      kentanKuuntelijat = new Set()
      kuuntelijat.set(tila, kentanKuuntelijat)
    }
    kentanKuuntelijat.add(kuuntelija)
    const efekti = effect(
      () => {
        const arvo = tila.value()
        if (!Object.is(arvo, edellinen)) {
          edellinen = arvo
          untracked(() => tilaaja.next(arvo as T))
        }
      },
      { injector },
    )
    const lopetaTuhoutuessa = injector.get(DestroyRef).onDestroy(() => tilaaja.complete())
    return () => {
      efekti.destroy()
      kentanKuuntelijat.delete(kuuntelija)
      lopetaTuhoutuessa()
    }
  })
}

/**
 * Asettaa kentän arvon kuten reaktiivisen lomakkeen `setValue`: kentän ja sen alikenttien `arvonMuutokset`-tilaajat
 * saavat uuden arvon heti, alikentät ensin. `ilmoita: false` vastaa asetusta `emitEvent: false`.
 */
export function asetaArvo<T>(kentta: FieldTree<T>, arvo: T, asetukset?: { ilmoita?: boolean }): void {
  ;(kentta() as FieldState<T>).value.set(arvo)
  ilmoitaArvosta(kentta as FieldTree<unknown>, asetukset?.ilmoita ?? true)
}

/** Kuten reaktiivisen lomakkeen `reset(arvo)`: asettaa arvon, poistaa kosketus- ja muokkausmerkinnät ja ilmoittaa tilaajille. */
export function nollaa<T>(kentta: FieldTree<T>, arvo: T): void {
  ;(kentta() as FieldState<T>).reset(arvo)
  ilmoitaArvosta(kentta as FieldTree<unknown>, true)
}

function ilmoitaArvosta(kentta: FieldTree<unknown>, ilmoita: boolean) {
  const tila = kentta()
  const arvo = untracked(() => tila.value())
  if (Array.isArray(arvo) || (arvo !== null && typeof arvo === 'object' && Object.getPrototypeOf(arvo) === Object.prototype)) {
    const alikentat = Array.isArray(arvo)
      ? [...(kentta as unknown as Iterable<FieldTree<unknown>>)]
      : [...(kentta as unknown as Iterable<[string, FieldTree<unknown>]>)].map(([, alikentta]) => alikentta)
    // Valinnan arvona olevan olion ominaisuus, jonka arvo on undefined, ei ole kenttä
    for (const alikentta of alikentat.filter(alikentta => typeof alikentta === 'function')) {
      ilmoitaArvosta(alikentta, ilmoita)
    }
  }
  for (const kuuntelija of [...(kuuntelijat.get(tila) ?? [])]) {
    kuuntelija(arvo, ilmoita)
  }
}
